// Botón de ayuda fijo en la esquina del panel.
//
// La idea es que reportar un problema cueste lo mismo que mandar un WhatsApp:
// un cuadro de texto y un botón. Nada de pedir versión del navegador, pantalla
// ni pasos para reproducir — todo eso lo adjunta el servidor solo, porque el
// usuario ya está autenticado. Un formulario largo no se llena: la gente
// prefiere convivir con el error.
import React, { useState } from 'react';
import { api } from '../../utils/api';

const CORREO = 'soporte@tabularis.cl';
// El mismo número de CONTACTO en LandingApp; no se importa de ahí para no
// arrastrar la landing al bundle del panel.
const TELEFONO = '+56 9 6786 9656';

const Soporte: React.FC<{ pantalla?: string }> = ({ pantalla }) => {
  const [abierto, setAbierto] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState('');

  const enviar = async () => {
    if (mensaje.trim().length < 5) { setError('Cuéntanos brevemente qué pasó.'); return; }
    setEnviando(true); setError('');
    try {
      await api('/soporte', { method: 'POST', body: { mensaje, pantalla } });
      setListo(true); setMensaje('');
    } catch (e: any) {
      setError(e?.message || `No pudimos enviarlo. Escríbenos a ${CORREO}`);
    } finally { setEnviando(false); }
  };

  const cerrar = () => { setAbierto(false); setListo(false); setError(''); };

  return (
    <>
      <button
        onClick={() => setAbierto((a) => !a)}
        title="Ayuda y soporte"
        style={{
          position: 'fixed', right: 18, bottom: 'var(--soporte-bottom, 18px)', zIndex: 900,
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '9px 15px', borderRadius: 22, cursor: 'pointer',
          border: '1px solid rgba(212,175,55,0.45)', background: 'var(--dark-3,#141d2e)',
          color: 'var(--cream,#e8e0d0)', fontSize: 13.5, fontWeight: 500,
          boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
        }}
      >
        <span style={{ fontSize: 15 }}>💬</span> Ayuda
      </button>

      {abierto && (
        <div style={{
          position: 'fixed', right: 18, bottom: 'calc(var(--soporte-bottom, 18px) + 48px)', zIndex: 901, width: 'min(360px, calc(100vw - 36px))',
          background: 'var(--dark-3,#141d2e)', border: '1px solid rgba(212,175,55,0.30)',
          borderRadius: 12, padding: 16, boxShadow: '0 14px 40px rgba(0,0,0,0.42)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <strong style={{ fontSize: 14, color: 'var(--cream,#e8e0d0)' }}>¿Necesitas ayuda?</strong>
            <button onClick={cerrar} style={{ background: 'none', border: 'none', color: 'var(--text-muted,#8a9bb5)', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>

          {listo ? (
            <div style={{ fontSize: 13, color: 'var(--text-body,#8a9bb5)', lineHeight: 1.6 }}>
              ✓ Recibimos tu mensaje. Te respondemos al correo de tu cuenta.
              <div style={{ marginTop: 12 }}>
                <button className="p-btn-secondary" onClick={cerrar} style={{ fontSize: 12 }}>Cerrar</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted,#8a9bb5)', marginBottom: 8, lineHeight: 1.55 }}>
                Cuéntanos qué intentabas hacer, qué pasó y, si corresponde, en qué causa
                o cliente. Mientras más claro el mensaje, más rápido lo resolvemos.
              </div>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Ej: en la causa C-1234-2026 quise subir un escrito y se queda cargando, no termina nunca."
                rows={4}
                style={{
                  width: '100%', resize: 'vertical', fontSize: 13, padding: '8px 10px',
                  borderRadius: 7, border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(0,0,0,0.22)', color: 'var(--cream,#e8e0d0)', fontFamily: 'inherit',
                }}
              />
              {error && <div style={{ color: '#e57373', fontSize: 12.5, marginTop: 6 }}>{error}</div>}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10 }}>
                <a href={`mailto:${CORREO}`} style={{ fontSize: 12, color: 'var(--text-muted,#8a9bb5)' }}>
                  o escríbenos a <span style={{ textDecoration: 'underline' }}>{CORREO}</span>
                </a>
                <button className="p-btn-primary" onClick={enviar} disabled={enviando}
                  style={{ fontSize: 12.5, padding: '7px 14px', opacity: enviando ? 0.6 : 1, cursor: enviando ? 'not-allowed' : 'pointer' }}>
                  {enviando ? 'Enviando…' : 'Enviar'}
                </button>
              </div>
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 8, lineHeight: 1.55,
                border: '1px solid rgba(212,175,55,0.30)', background: 'rgba(212,175,55,0.07)',
                fontSize: 12.5, color: 'var(--text-body,#8a9bb5)',
              }}>
                <strong style={{ color: 'var(--cream,#e8e0d0)' }}>¿Es urgente?</strong> Si el problema
                es grave y necesitas una solución inmediata, llámanos al{' '}
                <a href={`tel:${TELEFONO.replace(/\s/g, '')}`} style={{ color: '#D4AF37', fontWeight: 600, whiteSpace: 'nowrap' }}>{TELEFONO}</a>
                {' '}a cualquier hora. Soporte 24 horas, todos los días.
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Soporte;
